import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Dialog, DialogContent, FormControlLabel, Grid } from '@material-ui/core';

import { addressesApi } from 'api/addresses';
import { procurementApi as api } from 'api/procurement';
import { DataTable, FormComponent, Notifications } from 'shared';
import { validEmail } from 'shared/utils/validation';

function StateCountryModal({
    country,
    countryOpts,
    editing,
    getProvinces,
    getVendors,
    handleProvChange,
    mainCountryCodes,
    provinceOpts,
    row,
    setEditing,
    setNotification,
}) {
    useEffect(() => {
        getProvinces(country.text);
    }, []);

    return (
        <Dialog open={editing} onClose={() => setEditing(false)} fullWidth width="400px">
            <DialogContent>
                <FormComponent
                    formFields={[
                        {
                            name: 'province',
                            label: 'State/Province',
                            inputElement: 'select',
                            gridValue: 6,
                            inputProps: {
                                opts: provinceOpts,
                                required: !!mainCountryCodes.includes(country.value),
                            },
                            initialValue: row.original.province,
                        },
                        {
                            name: 'country',
                            label: 'Country',
                            inputElement: 'autocomplete',
                            gridValue: 6,
                            inputProps: { opts: countryOpts, onChange: handleProvChange },
                            initialValue: country,
                        },
                    ]}
                    onSubmit={async (formData) => {
                        const sendParams = {
                            country: formData.country,
                            province: mainCountryCodes.includes(formData.country) ? formData.province : 'N/A',
                        };

                        const response = await api.updateVendor(row.original.id, sendParams);
                        if (response.status === false) {
                            let message = 'Error modifying shipper';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setNotification({ text: message, level: 'error' });
                        } else {
                            setNotification({ text: 'Shipper modified!', level: 'success' });
                            getVendors();
                        }
                        setEditing(false);
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

function StateCountryCell({ countryOpts, getVendors, row, setNotification }) {
    const [editing, setEditing] = useState(false);

    const OGstate = row.original.province;
    const OGcountry = row.original.country;

    const mainCountryCodes = ['US', 'MX', 'CA'];

    const [provinceOpts, setProvinceOpts] = useState([]);
    const [country, setCountry] = useState(countryOpts.find((c) => c.value === row.original.country));

    // Load state options
    const getProvinces = async (country) => {
        if (country === 'United States') {
            country = 'unitedstates';
        }

        if (country === undefined) {
            const pleaseSelect = { text: 'Please Select Country', value: 0 };
            setProvinceOpts([pleaseSelect]);
            return;
        }

        const response = await api.getProvinces(country);
        if (response.status === false) {
            setNotification({ text: `No provinces found ${response.msg}`, level: 'error' });
            const pleaseSelect = { text: 'Please Select Country', value: 0 };
            setProvinceOpts([pleaseSelect]);
            return;
        }

        const miniOpts = response.data.rows.map((row) => {
            const out = {};
            out.text = row.name;
            out.value = row.twoLetter;
            return out;
        });

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setProvinceOpts([pleaseSelect].concat(miniOpts));
    };

    const handleProvChange = async (e, newValue) => {
        e.preventDefault();
        if (newValue && newValue.value) {
            setCountry(newValue);
            if (mainCountryCodes.includes(newValue.value)) {
                getProvinces(newValue.text);
            } else {
                setProvinceOpts([]);
            }
        }
    };

    return (
        <div style={{ textAlign: 'center' }}>
            <div style={{ margin: 'auto' }}>{OGstate ? `${OGstate}, ${OGcountry}` : OGcountry}</div>
            <div>
                <Button id={`StateCountry_Edit_${row.id}`} onClick={() => setEditing(true)}>
                    Edit
                </Button>
            </div>
            {editing && (
                <StateCountryModal
                    editing={editing}
                    setEditing={setEditing}
                    row={row}
                    country={country}
                    countryOpts={countryOpts}
                    provinceOpts={provinceOpts}
                    handleProvChange={handleProvChange}
                    setNotification={setNotification}
                    getVendors={getVendors}
                    mainCountryCodes={mainCountryCodes}
                    getProvinces={getProvinces}
                />
            )}
        </div>
    );
}

// Vendors
function ViewVendor() {
    const [rows, setRows] = useState([]);
    const [countryOpts, setCountryOpts] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [activeOnly, setActiveOnly] = useState(true);

    // Load vendors
    const getVendors = async () => {
        const response = await api.getVendors(activeOnly);
        if (response.status === false) {
            setNotification({ text: `No vendors found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }
        setRows(response.data.rows);
    };

    // Load country options
    const getCountries = async () => {
        const response = await addressesApi.getCountries();
        if (response.status === false) {
            setNotification({ text: `No countries found ${response.msg}`, level: 'error' });
            setCountryOpts([]);
            return;
        }

        const countryAsOpts = response.data.rows.map((row) => {
            const out = {};
            out.text = row.name;
            out.value = row.countryCode;
            return out;
        });

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setCountryOpts([pleaseSelect].concat(countryAsOpts));
    };

    useEffect(() => {
        getVendors();
        getCountries();
    }, []);

    useEffect(() => {
        getVendors();
    }, [activeOnly]);

    const moqForOpts = [
        { text: 'Delivery', value: 'delivery' },
        { text: 'Pickup', value: 'pickup' },
    ];

    const uomOpts = [
        { text: 'lb', value: 'lb' },
        { text: 'pallet', value: 'pallet' },
        { text: 'case', value: 'case' },
    ];

    // Must match ecommerce VendorPaymentTypeEnum string values.
    const paymentTypeOpts = [
        { text: 'Pre Pay', value: 'Pre Pay' },
        { text: 'Pay Upon Receipt', value: 'Pay Upon Receipt' },
        { text: 'On Time', value: 'On Time' },
        { text: 'Priority Vendor', value: 'Priority Vendor' },
    ];

    const columns = [
        { accessor: 'id', Header: 'Vendor ID' },
        { accessor: 'name', Header: 'Name', editable: false },
        { accessor: 'email', Header: 'MPOC Email', editable: true },
        { accessor: 'email1', Header: 'Email 1', editable: true },
        { accessor: 'email2', Header: 'Email 2', editable: true },
        { accessor: 'email3', Header: 'Email 3', editable: true },
        { accessor: 'email4', Header: 'Email 4', editable: true },
        { accessor: 'office', Header: 'Office', editable: true },
        { accessor: 'cell', Header: 'Cell', editable: true },
        { accessor: 'leadTime', Header: 'Lead Time', editable: true },
        { accessor: 'lineOne', Header: 'Line One', editable: true },
        { accessor: 'lineTwo', Header: 'Line Two', editable: true },
        { accessor: 'city', Header: 'City', editable: true },
        { accessor: 'paymentTerms', Header: 'Payment Terms', editable: true },
        {
            accessor: 'paymentType',
            Header: 'Payment Type',
            editable: true,
            editProps: {
                type: 'select',
                options: paymentTypeOpts,
            },
        },
        { accessor: 'internalLogisticsEmail', Header: 'Internal Logistics Email', editable: true },
        {
            Header: 'State/Country',
            width: '150px',
            Cell: ({ row }) => StateCountryCell({ row, countryOpts, getVendors, setNotification }),
        },
        { accessor: 'zip', Header: 'Zip', editable: true },
        {
            accessor: 'moq1For',
            Header: 'MOQ 1 For',
            editable: true,
            editProps: {
                type: 'select',
                options: moqForOpts,
            },
        },
        {
            accessor: 'moq1Uom',
            Header: 'MOQ 1 UOM',
            editable: true,
            editProps: {
                type: 'select',
                options: uomOpts,
            },
        },
        { accessor: 'moq1Number', Header: 'MOQ 1 #', editable: true },
        {
            accessor: 'moq2For',
            Header: 'MOQ 2 For',
            editable: true,
            editProps: { type: 'select', options: moqForOpts },
        },
        { accessor: 'moq2Uom', Header: 'MOQ 2 UOM', editable: true, editProps: { type: 'select', options: uomOpts } },
        { accessor: 'moq2Number', Header: 'MOQ 2 #', editable: true },
        {
            accessor: 'active',
            Header: 'Active',
            Cell: ({ row }) => {
                const isActive = Boolean(row.original.active);

                const handleToggle = async () => {
                    const update = { active: !isActive };

                    const response = await api.updateVendor(row.original.id, update);
                    if (response.status === false) {
                        let message = 'Error modifying vendor';
                        if (response) {
                            message += `: ${response.msg}`;
                        }
                        setNotification({ text: message, level: 'error' });
                    } else {
                        setNotification({ text: 'Vendor modified!', level: 'success' });
                        getVendors();
                    }
                };

                return (
                    <div style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={isActive} onChange={handleToggle} />
                    </div>
                );
            },
        },
    ];

    return (
        <div>
            <h1>Vendors</h1>
            <Notifications options={notification} />
            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            id="ActiveOnly_checkbox"
                            onChange={(event) => {
                                setActiveOnly(event.target.checked);
                            }}
                            color="primary"
                            checked={activeOnly}
                        />
                    }
                    label="Show Active Only"
                />
            </Grid>
            <div>
                <DataTable
                    columns={columns}
                    data={rows}
                    editable
                    saveEdit={async ({ field, id, value }) => {
                        // Validate email fields
                        if (field.startsWith('email')) {
                            if (field === 'email') {
                                // Main 'email' field must be valid and cannot be empty
                                if (!validEmail(value)) {
                                    setNotification({
                                        text: 'MPOC Email must be a valid email address',
                                        level: 'error',
                                    });
                                    return;
                                }
                            } else {
                                // For email1, email2, email3, email4: allow empty string or valid email
                                if (value !== '' && !validEmail(value)) {
                                    setNotification({
                                        text: 'Invalid email address. Leave blank to remove.',
                                        level: 'error',
                                    });
                                    return;
                                }
                            }
                        }

                        const update = {};
                        update[field] = value;

                        const response = await api.updateVendor(id, update);
                        if (response.status === false) {
                            let message = 'Error modifying vendor';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setNotification({ text: message, level: 'error' });
                        } else {
                            setNotification({ text: 'Vendor modified!', level: 'success' });
                            getVendors();
                        }
                    }}
                />
            </div>
        </div>
    );
}

export { ViewVendor, StateCountryCell, StateCountryModal };
